import React, { useEffect, useState } from 'react';
import { useApiClient } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import CustomLoader from './CustomLoader';

interface CurrentSponsorsProps {
  className?: string;
  compact?: boolean;
}

interface SponsorStats {
  sponsorId: string;
  sponsorName: string;
  sponsorEmail: string;
  sponsorLinkedIn: string;
  numberOfEvents: number;
  eventNames: string[];
  totalBounties: number;
  activeBounties: number;
  totalParticipants: number;
}

interface CurrentSponsorsData {
  sponsors: SponsorStats[];
  totalSponsors: number;
  totalEvents: number;
}

export const CurrentSponsors: React.FC<CurrentSponsorsProps> = ({ className = '', compact = false }) => {
  const [stats, setStats] = useState<CurrentSponsorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await apiClient.getCurrentSponsors();

        if (response.success && response.data) {
          setStats(response.data);
          setError(null);
        } else {
          setError(response.error || 'Failed to fetch current sponsors');
        }
      } catch (err) {
        setError('An error occurred while fetching sponsors');
        console.error('Error fetching current sponsors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [apiClient]);

  if (loading) {
    return (
      <CustomLoader />
    );
  }

  if (error) {
    return (
      <div className={`w-full my-8 ${className}`}>
        <div className="text-center p-8 text-red-600">{error}</div>
      </div>
    );
  }

  if (!stats || !stats.sponsors || stats.sponsors.length === 0) {
    return (
      <div className={`w-full my-8 ${className}`}>
        <div className="text-center p-8 text-gray-600">No current sponsors data available</div>
      </div>
    );
  }

  return (
    <div className={`w-full ${compact ? '' : 'my-8'} ${className}`}>
      <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto mb-8'}`}>
        {stats.sponsors.map((sponsor) => (
          <Card key={sponsor.sponsorId} className={`bg-white hover:shadow-lg transition-shadow duration-300 ${compact ? 'border-l-4 border-l-purple-500' : ''}`}>
            <CardContent className={compact ? "p-3 flex items-center gap-3" : "p-4"}>
              {compact ? (
                // Compact View
                <>
                  <div className="text-2xl opacity-70 leading-none">🏢</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{sponsor.sponsorName}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {sponsor.activeBounties} Active Bounties
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">{sponsor.totalParticipants}</div>
                    <div className="text-[10px] text-gray-500 uppercase">People</div>
                  </div>
                </>
              ) : (
                // Full View
                <>
                  {/* Sponsor Header */}
                  <div className="flex items-start gap-3 mb-3 pb-3 border-b border-gray-100">
                    <div className="text-3xl opacity-70 leading-none mt-0.5">🏢</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-base text-gray-900 truncate leading-tight mb-1">{sponsor.sponsorName}</div>
                      <div className="text-xs text-gray-500">
                        {sponsor.numberOfEvents} Event{sponsor.numberOfEvents !== 1 ? 's' : ''} • {sponsor.totalBounties} Bounties
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-100">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{sponsor.totalBounties}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{sponsor.activeBounties}</div>
                      <div className="text-xs text-gray-500">Active</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{sponsor.totalParticipants}</div>
                      <div className="text-xs text-gray-500">People</div>
                    </div>
                  </div>

                  {/* Events */}
                  {sponsor.eventNames && sponsor.eventNames.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Events</div>
                      <div className="space-y-0.5">
                        {sponsor.eventNames.slice(0, 2).map((name, idx) => (
                          <div key={idx} className="text-xs text-gray-700 truncate">{name}</div>
                        ))}
                        {sponsor.eventNames.length > 2 && (
                          <div className="text-xs text-gray-500">+{sponsor.eventNames.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {(sponsor.sponsorEmail || sponsor.sponsorLinkedIn) && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact</div>
                      {sponsor.sponsorEmail && (
                        <div className="text-xs mb-1.5">
                          <a href={`mailto:${sponsor.sponsorEmail}`} className="text-blue-600 hover:underline truncate block">
                            {sponsor.sponsorEmail}
                          </a>
                        </div>
                      )}
                      {sponsor.sponsorLinkedIn && (
                        <div className="text-xs">
                          <a href={sponsor.sponsorLinkedIn} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            LinkedIn
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Section - Hide in compact mode */}
      {!compact && (
        <div className="flex gap-6 justify-center flex-wrap p-5 bg-white border border-gray-200 rounded-lg shadow-sm max-w-lg mx-auto">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Sponsors</span>
            <span className="text-3xl font-bold text-gray-900">{stats.totalSponsors}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Events</span>
            <span className="text-3xl font-bold text-gray-900">{stats.totalEvents}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentSponsors;
